import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import BusinessIcon from '@material-ui/icons/Business';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import WarningIcon from '@material-ui/icons/Warning';
import BreadcrumbLayout from '../../../shared/components/common/breadcrumbLayout';
import { Modal, PrimaryButton, SecondaryButton } from '../../../shared/ui';
import {
    MAINTENANCE_TYPE,
    getMaintenanceStyle,
} from '../../../shared/utils/maintenanceTypeHelpers';
import {
    AddressSectionTitle,
    AddressSectionWrapper,
    CartActions,
    CartSummary,
    EmptyState,
    EstimateLayoutContainer,
    GroupHeader,
    GroupMeta,
    GroupPill,
    GroupStack,
    GroupTitle,
    MainContent,
    MaintenanceBody,
    MaintenanceGroupWrapper,
    QuoteItemActions,
    QuoteItemCard,
    QuoteItemDetails,
    QuoteItemHeader,
    QuoteItemTitle,
    QuoteItemsList,
    WarningBox,
} from '../styles';

const LOCATION = {
    WORKSHOP: 'oficina',
    COMPANY: 'empresa',
};

function resolveTaskNames(selectedTasks, availableTasks) {
    if (!Array.isArray(selectedTasks) || selectedTasks.length === 0) return [];

    return selectedTasks
        .map((task) => {
            if (task && typeof task === 'object') return task.nome || task.descricao || '';

            const taskId = Number(task);
            const taskFromCatalog = availableTasks.find((availableTask) => Number(availableTask.id) === taskId);
            if (taskFromCatalog) return taskFromCatalog.nome;

            return String(task || '');
        })
        .filter(Boolean);
}

function ClientQuoteCartView({
    items,
    availableTasks,
    onAddNew,
    onEditItem,
    onDeleteItem,
    onSend,
    loading,
    currentLanguage,
}) {
    const clientLanguage = currentLanguage.bepiService.client;
    const sharedLanguage = currentLanguage.bepiService.shared;
    const [itemPendingDelete, setItemPendingDelete] = useState(null);

    const groupedItems = useMemo(() => {
        return items.reduce(
            (acc, item) => {
                const location = item.maintenanceLocalization === LOCATION.COMPANY ? LOCATION.COMPANY : LOCATION.WORKSHOP;

                if (location === LOCATION.WORKSHOP) {
                    acc.workshop.push(item);
                    return acc;
                }

                const addressKey = String(item.companyAddress || '-');
                if (!acc.companyByAddress[addressKey]) {
                    acc.companyByAddress[addressKey] = [];
                }

                acc.companyByAddress[addressKey].push(item);
                return acc;
            },
            {
                workshop: [],
                companyByAddress: {},
            }
        );
    }, [items]);

    const companyAddresses = useMemo(
        () => Object.keys(groupedItems.companyByAddress),
        [groupedItems]
    );

    const totalSummary = useMemo(() => {
        const totalItemsLabel = items.length === 1
            ? clientLanguage.client_quote_cart_item_singular
            : clientLanguage.client_quote_cart_item_plural;

        const workshopText = groupedItems.workshop.length > 0
            ? String(clientLanguage.client_quote_cart_in_oficina_suffix || '').replace('#vr0#', groupedItems.workshop.length)
            : '';

        const companyText = companyAddresses.length > 0
            ? String(clientLanguage.client_quote_cart_addresses_suffix || '')
                .replace('#vr0#', companyAddresses.length)
                .replace(
                    '#vr1#',
                    companyAddresses.length === 1
                        ? clientLanguage.client_quote_cart_address_singular
                        : clientLanguage.client_quote_cart_address_plural
                )
            : '';

        return `${clientLanguage.client_quote_cart_total_label}${items.length} ${totalItemsLabel}${workshopText}${companyText}`;
    }, [
        clientLanguage,
        companyAddresses.length,
        groupedItems.workshop.length,
        items.length,
    ]);

    const breadcrumbItems = useMemo(
        () => [
            { label: clientLanguage.client_quote_cart_home_breadcrumb, path: '/bepi/client' },
            { label: clientLanguage.client_quote_cart_quote_breadcrumb, onClick: onAddNew },
            { label: clientLanguage.client_quote_cart_page_title },
        ],
        [clientLanguage, onAddNew]
    );

    const renderItem = (item) => {
        const details = [
            `${clientLanguage.client_quote_cart_qty_label}${item.quantity || 1}`,
            item.model ? `${clientLanguage.client_quote_cart_model_label}${item.model}` : '',
            item.brand ? `${clientLanguage.client_quote_cart_brand_label}${item.brand}` : '',
            item.problem ? `${clientLanguage.client_quote_cart_problem_label}${item.problem}` : '',
        ].filter(Boolean);

        const taskNames = resolveTaskNames(item.selectedTasks, availableTasks);
        if (taskNames.length) {
            details.push(`${clientLanguage.client_quote_cart_tasks_label}${taskNames.join(', ')}`);
        }

        return (
            <QuoteItemCard key={item.id}>
                <QuoteItemHeader>
                    <QuoteItemTitle>{item.equipamento || item.nome || '-'}</QuoteItemTitle>
                    <QuoteItemActions>
                        <SecondaryButton size='sm' type='button' onClick={() => onEditItem(item)}>
                            {sharedLanguage.shared_clients_edit_btn}
                        </SecondaryButton>
                        <SecondaryButton
                            size='sm'
                            type='button'
                            onClick={() => setItemPendingDelete(item)}
                        >
                            {sharedLanguage.shared_details_delete_tooltip}
                        </SecondaryButton>
                    </QuoteItemActions>
                </QuoteItemHeader>

                <QuoteItemDetails>
                    {details.map((detailLine) => (
                        <li key={`${item.id}-${detailLine}`}>{detailLine}</li>
                    ))}
                </QuoteItemDetails>
            </QuoteItemCard>
        );
    };

    const workshopStyle = getMaintenanceStyle(MAINTENANCE_TYPE.OFICINA);
    const companyStyle = getMaintenanceStyle(MAINTENANCE_TYPE.INCOMPANY);

    return (
        <BreadcrumbLayout
            title={clientLanguage.client_quote_cart_page_title}
            breadcrumbItems={breadcrumbItems}
            currentLanguage={currentLanguage}
            showBackButton
        >
            <MainContent>
                <Modal
                    isOpen={Boolean(itemPendingDelete)}
                    type='warning'
                    title={clientLanguage.client_quote_cart_delete_title}
                    message={clientLanguage.client_quote_cart_delete_warning}
                    onConfirm={() => {
                        if (!itemPendingDelete) return;
                        onDeleteItem(itemPendingDelete.id);
                        setItemPendingDelete(null);
                    }}
                    onCancel={() => setItemPendingDelete(null)}
                    confirmText={clientLanguage.client_quote_cart_delete_confirm}
                    cancelText={sharedLanguage.shared_details_cancel_btn}
                />

                <EstimateLayoutContainer>
                    {items.length === 0 ? (
                        <EmptyState>{clientLanguage.client_quote_cart_empty_title} {clientLanguage.client_quote_cart_empty_subtitle}</EmptyState>
                    ) : (
                        <GroupStack>
                            {companyAddresses.length > 1 ? (
                                <WarningBox>
                                    <strong>
                                        <WarningIcon fontSize='small' /> {clientLanguage.client_quote_cart_multiple_addresses_title}
                                    </strong>
                                    <p>
                                        {String(clientLanguage.client_quote_cart_multiple_addresses_text || '').replace('#vr0#', companyAddresses.length)}
                                    </p>
                                </WarningBox>
                            ) : null}

                            {groupedItems.workshop.length > 0 ? (
                                <MaintenanceGroupWrapper
                                    $accentColor={workshopStyle.indicatorColor}
                                    $softBackground={workshopStyle.chipBackground}
                                >
                                    <GroupHeader
                                        $indicatorColor={workshopStyle.indicatorColor}
                                        $background={workshopStyle.indicatorColor}
                                    >
                                        <GroupTitle>
                                            <BusinessIcon fontSize='small' />
                                            {clientLanguage.client_quote_cart_oficina_title}
                                        </GroupTitle>
                                        <GroupMeta>
                                            {groupedItems.workshop.length} {groupedItems.workshop.length === 1 ? clientLanguage.client_quote_cart_item_singular : clientLanguage.client_quote_cart_item_plural}
                                            <GroupPill
                                                $background={workshopStyle.chipBackground}
                                                $textColor={workshopStyle.chipColor}
                                                $borderColor={workshopStyle.borderColor}
                                            >
                                                {clientLanguage.client_quote_cart_in_oficina_chip}
                                            </GroupPill>
                                        </GroupMeta>
                                    </GroupHeader>

                                    <MaintenanceBody>
                                        <QuoteItemsList>
                                            {groupedItems.workshop.map(renderItem)}
                                        </QuoteItemsList>
                                    </MaintenanceBody>
                                </MaintenanceGroupWrapper>
                            ) : null}

                            {companyAddresses.map((address) => (
                                <MaintenanceGroupWrapper
                                    key={address}
                                    $accentColor={companyStyle.indicatorColor}
                                    $softBackground={companyStyle.chipBackground}
                                >
                                    <GroupHeader
                                        $indicatorColor={companyStyle.indicatorColor}
                                        $background={companyStyle.indicatorColor}
                                    >
                                        <GroupTitle>
                                            <LocationOnIcon fontSize='small' />
                                            {clientLanguage.client_quote_cart_empresa_title}
                                        </GroupTitle>
                                        <GroupMeta>
                                            {groupedItems.companyByAddress[address].length} {groupedItems.companyByAddress[address].length === 1 ? clientLanguage.client_quote_cart_item_singular : clientLanguage.client_quote_cart_item_plural}
                                            <GroupPill
                                                $background={companyStyle.chipBackground}
                                                $textColor={companyStyle.chipColor}
                                                $borderColor={companyStyle.borderColor}
                                            >
                                                {clientLanguage.client_quote_cart_in_client_chip}
                                            </GroupPill>
                                        </GroupMeta>
                                    </GroupHeader>

                                    <MaintenanceBody>
                                        <AddressSectionWrapper>
                                            <AddressSectionTitle>
                                                {clientLanguage.client_quote_cart_address_label} {address}
                                            </AddressSectionTitle>

                                            <QuoteItemsList>
                                                {groupedItems.companyByAddress[address].map(renderItem)}
                                            </QuoteItemsList>
                                        </AddressSectionWrapper>
                                    </MaintenanceBody>
                                </MaintenanceGroupWrapper>
                            ))}

                            <CartSummary>{totalSummary}</CartSummary>
                        </GroupStack>
                    )}

                    <CartActions>
                        <SecondaryButton type='button' onClick={onAddNew}>
                            {items.length === 0
                                ? clientLanguage.client_quote_cart_add_first_btn
                                : clientLanguage.client_quote_cart_add_new_btn}
                        </SecondaryButton>
                        <PrimaryButton type='button' onClick={onSend} disabled={loading || items.length === 0}>
                            {clientLanguage.client_quote_cart_request_quotes_btn}
                        </PrimaryButton>
                    </CartActions>
                </EstimateLayoutContainer>
            </MainContent>
        </BreadcrumbLayout>
    );
}

ClientQuoteCartView.propTypes = {
    items: PropTypes.arrayOf(PropTypes.object),
    availableTasks: PropTypes.arrayOf(PropTypes.object),
    onAddNew: PropTypes.func.isRequired,
    onEditItem: PropTypes.func.isRequired,
    onDeleteItem: PropTypes.func.isRequired,
    onSend: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    currentLanguage: PropTypes.object.isRequired,
};

ClientQuoteCartView.defaultProps = {
    items: [],
    availableTasks: [],
    loading: false,
};

export default ClientQuoteCartView;
